import { useCallback, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Button,
  Caption,
  Input,
  SegmentedControl,
  Section,
  Select,
  Chip,
} from '@telegram-apps/telegram-ui';
import { useUpdateProduct } from '../hooks/useProductMutations';
import { api } from '../lib/api';
import { formatImageUrl } from '../lib/imageUrl';
import type { Product, ProductStatus } from '../types/product';

const STATUS_ITEMS: ProductStatus[] = ['draft', 'active', 'archived'];

const STATUS_LABEL_SEG: Record<ProductStatus, string> = {
  draft: 'Черновик',
  active: 'Актив',
  archived: 'Архив',
};

const PRESET_CATEGORIES = ['Верхняя одежда', 'Платья', 'Брюки/Джинсы', 'Футболки/Топы', 'Обувь', 'Аксессуары'];
const PRESET_TAGS = ['Лето', 'Зима', 'Демисезон', 'Новинка', 'Распродажа', 'База', 'Спорт'];

const CATEGORY_LABELS: Record<string, string> = {
  dress: 'Платье',
  top: 'Топ/Блузка',
  bottom: 'Низ',
  outerwear: 'Верхняя одежда',
  shoes: 'Обувь',
  bag: 'Сумка',
  accessory: 'Аксессуар',
  other: 'Другое',
};

interface CatalogAnalysisResult {
  ok: boolean;
  name: string;
  category: string;
  tags: string[];
  sku: string;
}

interface CatalogAnalyzeResponse {
  results: CatalogAnalysisResult[];
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function uniqueNonEmpty(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

interface Props {
  product: Product;
  onDone: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  deletePending?: boolean;
  deleteError?: Error | null;
}

export function ProductEditForm({ product, onDone, onCancel, onDelete, deletePending = false, deleteError }: Props) {
  const [name, setName] = useState(product.name);
  const [sku, setSku] = useState(product.sku ?? '');
  const [category, setCategory] = useState(product.category ?? PRESET_CATEGORIES[0]);
  const [tags, setTags] = useState<string[]>(product.tags);
  const [status, setStatus] = useState<ProductStatus>(product.status);
  const [aiFilling, setAiFilling] = useState(false);
  const [aiError, setAiError] = useState('');

  const update = useUpdateProduct();
  const categoryOptions = useMemo(
    () => uniqueNonEmpty([...PRESET_CATEGORIES, category]),
    [category],
  );
  const tagOptions = useMemo(() => uniqueNonEmpty([...PRESET_TAGS, ...tags]), [tags]);

  const submit = useCallback(() => {
    if (!name.trim()) return;

    update.mutate(
      {
        id: product.id,
        data: {
          name: name.trim(),
          sku: sku.trim() || null,
          category: category.trim() || null,
          tags: tags,
          status,
          buyLink: null,
        },
      },
      { onSuccess: () => onDone() },
    );
  }, [name, sku, category, tags, status, product.id, update, onDone]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  const fillWithAi = useCallback(async () => {
    const imageUrl = formatImageUrl(product.preprocessedImageUrl ?? product.sourceImageUrl);
    if (!imageUrl) {
      setAiError('У товара нет фото для анализа');
      return;
    }

    setAiFilling(true);
    setAiError('');
    try {
      const res = await fetch(imageUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const base64 = await blobToBase64(blob);
      const data = await api<CatalogAnalyzeResponse>('/catalog/analyze', {
        method: 'POST',
        body: JSON.stringify({
          images: [
            {
              base64,
              mimeType: blob.type || 'image/jpeg',
              filename: `${product.id}.jpg`,
            },
          ],
        }),
      });
      const result = data.results[0];
      if (!result) throw new Error('empty result');

      setName(result.name || product.name);
      setSku(result.sku || '');
      setCategory(CATEGORY_LABELS[result.category] ?? result.category ?? '');
      setTags(uniqueNonEmpty(result.tags ?? []));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось заполнить поля';
      setAiError(message);
    } finally {
      setAiFilling(false);
    }
  }, [product.id, product.name, product.preprocessedImageUrl, product.sourceImageUrl]);

  const toggleTag = (tag: string) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Section header="Редактирование">
        <Input header="Название *" required value={name} onChange={(ev) => setName(ev.target.value)} />
        <Input header="SKU" placeholder="SKU" value={sku} onChange={(ev) => setSku(ev.target.value)} />
        <div style={{ padding: '0 20px 12px' }}>
          <Button
            mode="outline"
            size="m"
            stretched
            type="button"
            loading={aiFilling}
            disabled={aiFilling || update.isPending}
            onClick={() => void fillWithAi()}
          >
            Заполнить поля с AI
          </Button>
          {aiError && (
            <Caption style={{ display: 'block', marginTop: 8, color: 'var(--tgui--destructive_text_color)' }}>
              {aiError}
            </Caption>
          )}
        </div>
        
        <Select 
          header="Категория" 
          value={category} 
          onChange={(e) => setCategory(e.target.value)}
        >
          {categoryOptions.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </Select>

        <div style={{ padding: '12px 20px' }}>
          <Caption style={{ marginBottom: 8, display: 'block' }}>Теги</Caption>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tagOptions.map((tag) => {
              const isActive = tags.includes(tag);
              return (
                <Chip
                  key={tag}
                  mode={isActive ? 'elevated' : 'outline'}
                  onClick={() => toggleTag(tag)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: isActive ? 'var(--tg-theme-button-color, #007aff)' : 'transparent',
                    color: isActive ? 'var(--tg-theme-button-text-color, #ffffff)' : 'inherit',
                  }}
                >
                  {tag}
                </Chip>
              );
            })}
          </div>
        </div>

        <Caption level="2" style={{ marginBottom: 4, marginTop: 8, paddingLeft: 20, display: 'block' }}>
          Статус
        </Caption>
        <div style={{ overflowX: 'auto', paddingBottom: 8, paddingLeft: 16, paddingRight: 16 }}>
          <SegmentedControl>
            {STATUS_ITEMS.map((s) => (
              <SegmentedControl.Item
                key={s}
                selected={status === s}
                type="button"
                onClick={() => setStatus(s)}
              >
                {STATUS_LABEL_SEG[s]}
              </SegmentedControl.Item>
            ))}
          </SegmentedControl>
        </div>
      </Section>

      {update.isError && (
        <Caption style={{ padding: '0 16px 8px', color: 'var(--tgui--destructive_text_color)' }}>
          {(update.error as Error)?.message ?? 'Не удалось сохранить'}
        </Caption>
      )}

      <Section header=" ">
        <Button
          mode="filled"
          size="m"
          stretched
          type="submit"
          disabled={update.isPending}
          loading={update.isPending}
        >
          Сохранить
        </Button>
        <Button mode="plain" size="m" stretched type="button" disabled={update.isPending} onClick={onCancel}>
          Отмена
        </Button>
      </Section>

      {onDelete && (
        <Section header="Удаление">
          {deleteError && (
            <Caption style={{ display: 'block', padding: '0 16px 8px', color: 'var(--tgui--destructive_text_color)' }}>
              {deleteError.message || 'Не удалось удалить товар'}
            </Caption>
          )}
          <Button
            mode="plain"
            size="m"
            stretched
            type="button"
            disabled={deletePending || update.isPending}
            loading={deletePending}
            onClick={onDelete}
            style={{ color: 'var(--tgui--destructive_text_color)' }}
          >
            Удалить товар
          </Button>
        </Section>
      )}
    </form>
  );
}

